import React, { useEffect, useState } from "react";
import styles from "../EditDiv/EditDiv.module.css";
import buttonStyles from "../YesNoButtonsCSS/YesNoButtons.module.css";
import BlueLogo from "../../assets/BlueLogo.png";
import { Grid, TextField } from "@mui/material";
import { Form, FormikProvider } from "formik";
import { useFormik } from "formik";
import * as Yup from "yup";
import textfieldStyle from "../../GlobalStyles/styles";
import Dropdown from '../DropDownBox/Dropdown';

const TaskDetailsForm = ({ projectData, onSubmit, onClose }) => {
    const statusOptions = ['S3 Prep', 'S3 End', 'S4 Prep', 'S4 End', 'Finish'];
    const costRankingOptions = ['A - Profitable', 'B - Broke Even', 'C - Lost money'];
    const timeRankingOptions = ['A - Ahead of the deadline', 'B - Met the deadline', 'C - Late'];
    const qualityRankingOptions = ['A - Very Satisfied', 'B - There were comments', 'C - Not good enough'];

    // Add state for task options
    const [taskOptions, setTaskOptions] = useState([
        "Site Visit",
        "Schemes",
        "Calculations",
        "Drawings",
        "RC Detailing",
        "Reports",
    ]);

    // Function to add custom tasks to the dropdown options
    const handleAddCustomTask = (newValue) => {
        if (newValue && !taskOptions.includes(newValue)) {
            setTaskOptions((prevOptions) => [...prevOptions, newValue]);
        }
    };

    const handleFormClick = (e) => {
        e.stopPropagation();
    };

    // Convert taskOutsourced string to array for initial value
    const initialTaskOutsourced = projectData?.taskOutsourced
        ? projectData.taskOutsourced.split(',').map(t => t.trim())
        : [];

    const initialValues = {
        ID: projectData?.ID || "",
        projectNumber: projectData?.projectNumber || "",
        runner: projectData?.runner || "",
        taskOutsourced: initialTaskOutsourced, // Use the converted array
        projectAddress: projectData?.projectAddress || "",
        status: projectData?.status || "",
        feeEarnt: projectData?.totalFeeEarnt || "",
        internalCost: projectData?.totalInternalCost || "",
        subcontractorPaid: projectData?.subcontractorPaid || "",
        wasSuccessful: projectData?.wasSuccessful || "",
        costRanking: projectData?.costRanking || "",
        timeRanking: projectData?.timeRanking || "",
        qualityRanking: projectData?.qualityRanking || "",
        finalComment: projectData?.finalComment || "",
    };

    // Add validation for taskOutsourced
    const validationSchema = Yup.object().shape({
        status: Yup.string(),
        subcontractorPaid: Yup.number().typeError("Subcontractor Cost must be a number").min(0, "Subcontractor Cost must be positive"),
        taskOutsourced: Yup.array()
            .min(1, "At least one task must be selected")
            .required("Task Outsourced is required"),
    });

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            // Convert taskOutsourced array back to string for submission if needed
            const { feeEarnt, ...valuesWithoutFeeEarnt } = values;
            const submissionValues = {
                ...valuesWithoutFeeEarnt,
                taskOutsourced: values.taskOutsourced.join(', '),
            };
            onSubmit(submissionValues); // Submit the modified values
            onClose();
            console.log(submissionValues);
        },
    });

    const { touched, errors, handleSubmit, values, setFieldValue } = formik;

    // Determine if ranking fields should be disabled based on status including "finish"
    const isRankingDisabled = !values.status?.toLowerCase().includes('finish');

    useEffect(() => {
        const { costRanking, timeRanking, qualityRanking } = values;
        const hasInvalidRanking = [costRanking, timeRanking, qualityRanking].some(
            ranking => ranking?.charAt(0) === 'C' || ranking?.charAt(0) === 'D'
        );
        setFieldValue('wasSuccessful', hasInvalidRanking ? 'No' : 'Yes');
    }, [values.costRanking, values.timeRanking, values.qualityRanking, setFieldValue]);

    useEffect(() => {
        // Automatically determine Cost Ranking based on Internal Cost vs. Fee Earnt
        const fee = parseFloat(values.feeEarnt);
        const cost = parseFloat(values.internalCost);

        if (!isNaN(fee) && !isNaN(cost) && fee > 0) {
            let newRanking = "";
            if (cost <= fee * 1.1) {
                newRanking = "A - Profitable";
            } else if (cost <= fee * 1.6) {
                newRanking = "B - Broke Even";
            } else {
                newRanking = "C - Lost money";
            }

            if (newRanking && newRanking !== values.costRanking) {
                setFieldValue("costRanking", newRanking);
            }
        }
    }, [values.feeEarnt, values.internalCost, setFieldValue, values.costRanking]);

    const renderField = (label, fieldName, type = "text", isDropdown = false, options = [], disabled = false, multiline = false) => (
        <Grid container sx={{ width: "100%" }}>
            <Grid item xs={3} sx={{ pr: 3 }}>
                <p className={styles.dataTitleV2}>{label}: </p>
            </Grid>
            <Grid item xs={9} sx={{ mt: 1 }}>
                {isDropdown ? (
                    <Dropdown
                        options={options}
                        initialSelectedValue={formik.values[fieldName]}
                        placeholder={`Select ${label}`}
                        onValueChange={(newValue) => formik.setFieldValue(fieldName, newValue)}
                        PopUp={true}
                        errorState={Boolean(touched[fieldName] && errors[fieldName])}
                        disabled={disabled}
                    />
                ) : (
                    <TextField
                        fullWidth
                        value={formik.values[fieldName]}
                        type={type}
                        onChange={(e) => formik.setFieldValue(fieldName, e?.target?.value)}
                        inputProps={{
                            style: {
                                color: formik.values[fieldName] ? "black" : "inherit",
                                cursor: disabled ? "not-allowed" : "text",
                            },
                            disabled: disabled,
                        }}
                        placeholder={label}
                        sx={textfieldStyle}
                        error={Boolean(touched[fieldName] && errors[fieldName])}
                        helperText={touched[fieldName] && errors[fieldName]}
                        multiline={multiline}
                        rows={multiline ? 1.5 : 1}
                    />
                )}
            </Grid>
        </Grid>
    );

    return (
        <div className={styles.backGround} style={{ position: "fixed", alignItems: "flex-start" }}>
            <div className={styles.form} style={{ width: "500px" }} onClick={handleFormClick}>
                <div className={styles.momentTitle}>MOMENT</div>
                <div className={styles.sectionTitle}>Update Task Details</div>

                <FormikProvider value={formik}>
                    <Form autoComplete="off" noValidate style={{ width: "100%" }}>
                        {renderField("Runner", "runner", "text", false, [], true)}
                        
                        {/* Replace TextField rendering with Dropdown for Task Outsourced */}
                        <Grid container sx={{ width: "100%" }}>
                            <Grid item xs={3} sx={{ pr: 3 }}>
                                <p className={styles.dataTitleV2}>Task Outsourced: </p>
                            </Grid>
                            <Grid item xs={9} sx={{ mt: 1 }}>
                                <Dropdown
                                    options={taskOptions}
                                    initialSelectedValue={formik.values.taskOutsourced}
                                    placeholder={"Select or Add Tasks"}
                                    onValueChange={(newValue) =>
                                        formik.setFieldValue("taskOutsourced", newValue)
                                    }
                                    mode='multi'
                                    allowOtherOption={true}
                                    onAddNew={handleAddCustomTask}
                                    PopUp={true}
                                    errorState={Boolean(touched.taskOutsourced && errors.taskOutsourced)}
                                    helperText={touched.taskOutsourced && errors.taskOutsourced} // Pass helperText for validation message
                                />
                                {touched.taskOutsourced && errors.taskOutsourced && ( // Manually display error message if needed, though Dropdown might handle it via helperText
                                     <p style={{ color: "red", fontSize: "12px", marginLeft: "14px" }}>{errors.taskOutsourced}</p>
                                 )}
                            </Grid>
                        </Grid>

                        {renderField("Project Address", "projectAddress", "text", false, [], true)}
                        {renderField("Status", "status", "text", true, statusOptions)}
                        {renderField("Total Fee Earnt", "feeEarnt", "number", false, [], true)}
                        {renderField("Internal Cost", "internalCost", "number", false, [], true)}
                        {renderField("Subcontractor Paid", "subcontractorPaid", "number")}
                        {renderField("Cost Ranking", "costRanking", "text", true, costRankingOptions, true)}
                        {renderField("Time Ranking", "timeRanking", "text", true, timeRankingOptions, isRankingDisabled)}
                        {renderField("Quality Ranking", "qualityRanking", "text", true, qualityRankingOptions, isRankingDisabled)}
                        {renderField("Was this Project Successfully Outsourced?", "wasSuccessful", "text", false, [], true)}
                        {renderField("Final Comment", "finalComment", "text", false, [], false, true)}
                    </Form>
                </FormikProvider>

                <div className={styles.updateAndCancel}>
                    <div className={styles.updateButton} onClick={handleSubmit}>
                        <div className={buttonStyles.YesButtonComponentStyling}>UPDATE</div>
                    </div>
                    <div className={styles.cancelButton} onClick={onClose}>
                        <div className={buttonStyles.NoButtonComponentStyling}>CANCEL</div>
                    </div>
                </div>

                <img className={styles.blueLogo} src={BlueLogo} alt="BlueLogo" />
            </div>
        </div>
    );
};

export default TaskDetailsForm;
